import { getAdvancedRule, getDatesFromAdvancedRule, getNewConfirmedDates,
  areDatesEqual } from './utils/HotelRequestUtil';
import moment from 'moment';

/**
 * Hotel booking advanced rule validator class, handles hotel room booking
 * validation for checkin/check out date
 */
export default class HotelAdvancedRulesValidator {
  constructor(hotelRequestRules = [], currentAdmissionItemId, currentRegistrationTypeId, isPlanner,
    eventTimezone, hotels) {
    this.currentAdmissionItemId = currentAdmissionItemId;
    this.currentRegistrationTypeId = currentRegistrationTypeId;
    this.advancedRules = hotelRequestRules.filter(ar => ar.isActive && ar.admissionItem.length > 0
      && ar.contactType.length > 0) || [];
    this.isPlanner = isPlanner;
    this.eventTimezone = eventTimezone;
    this.getValidationResult = this.getValidationResult.bind(this);
    this.hotels = hotels;
  }

  getValidationResult(travelBookings) {
    if (this.advancedRules.length === 0 || !travelBookings || travelBookings.length === 0) {
      return { isValid: true };
    }
    // Get matching advanced rule
    const matchingAdvancedRule = getAdvancedRule(this.advancedRules, this.currentAdmissionItemId,
      this.currentRegistrationTypeId);

    const invalidHotelBookings = [];

    // Exit early if no rules match
    if (!matchingAdvancedRule) {
      // validate confirmed dates against hotel only
      /* say if advance rule was getting applied earlier and on reg type/admission item change.
       reg rule is not coming into picture due to which confirmed dates can change, we have to show conflict*/
      travelBookings.forEach(b => {
        b.hotelRoomBookings.forEach(hrb => {
          const hotel = this.hotels.find(h => h.id === hrb.hotelId);
          if (hotel && this.hasInvalidShoulderDatesForApprovalFromHotelInfo(hotel, hrb)) {
            invalidHotelBookings.push(hrb);
          }
        });
      });
    } else {
      // Get the effective check in and checkout date from advance rule
      const hotelAdvancedRuleDate = getDatesFromAdvancedRule(matchingAdvancedRule, this.isPlanner);

      // Verify each hotel booking within the travel bookings for the given matching advanced rule
      travelBookings.forEach(b => {
        b.hotelRoomBookings.forEach(hrb => {
          if (this.isHotelBookingRequestedDatesInvalid(hotelAdvancedRuleDate, hrb)) {
            invalidHotelBookings.push(hrb);
          } else if (this.hasInvalidShoulderDatesForApprovalFromRegRuleInfo(matchingAdvancedRule, hrb)) {
            invalidHotelBookings.push(hrb);
          }
        });
      });
    }
    return invalidHotelBookings.length > 0 ? { isValid: false, invalidHotelBookings } : { isValid: true };
  }

  isHotelBookingRequestedDatesInvalid(hotelAdvancedRuleDate, hotelRoomBooking) {
    return !(moment(hotelRoomBooking.checkinDate).isSameOrAfter(hotelAdvancedRuleDate.checkinDate)
      && moment(hotelRoomBooking.checkoutDate).isSameOrBefore(hotelAdvancedRuleDate.checkoutDate));
  }

  /**
   * handles shoulder date approval fields validation based on advance rule level setup
   * @param advanceRule
   * @param hotelRoomBooking
   * @returns {boolean}
   */
  hasInvalidShoulderDatesForApprovalFromRegRuleInfo(advanceRule, hotelRoomBooking) {
    // if existing confirmed checkin checkout dates are not defined and new advance rule also has approval off
    // then we dont need to validate it for shoulder date approval
    if (!hotelRoomBooking.confirmedCheckinDate && !hotelRoomBooking.confirmedCheckoutDate &&
      (!advanceRule.shoulderDateSetup || !advanceRule.shoulderDateSetup.isPlannerApprovalRequired)) {
      return false;
    }

    return this.recalculateAndValidateConfirmedDates(advanceRule.arrivalDate, advanceRule.departureDate,
      hotelRoomBooking);
  }

  /**
   * handles shoulder date approval fields validation based on hotel level setup
   * @param hotelData
   * @param hotelRoomBooking
   * @returns {boolean}
   */
  hasInvalidShoulderDatesForApprovalFromHotelInfo(hotelData, hotelRoomBooking) {
    // if existing confirmed checkin checkout dates are not defined and new advance rule also has approval off
    // then we don't need to validate it for shoulder date approval
    if (!hotelRoomBooking.confirmedCheckinDate && !hotelRoomBooking.confirmedCheckoutDate &&
      (!hotelData || !hotelData.allowRequestShoulderDays)) {
      return false;
    }

    // we are already ignoring time part of hotel level in event timezone while loading snapshot in state

    return this.recalculateAndValidateConfirmedDates(hotelData.arrivalDate, hotelData.departureDate, hotelRoomBooking);
  }

  /**
   * recalculating confirmed dates based on rules of new admission item/ regtype selection and then using it to validate
   * against existing confirmed dates. If they didnt match show conflict
   * @param earliestArrival
   * @param latestDeparture
   * @param hotelRoomBooking
   * @returns {boolean}
   */
  recalculateAndValidateConfirmedDates(earliestArrival, latestDeparture, hotelRoomBooking) {
    // returns recalculated confirmed dates in event timezone
    let newConfirmedDates = getNewConfirmedDates(earliestArrival, latestDeparture, hotelRoomBooking.checkinDate,
      hotelRoomBooking.checkoutDate);

    // if new calculated confirmed dates doesn't match with persisted confirmed dates show conflict
    return !areDatesEqual(newConfirmedDates.confirmedCheckinDate, hotelRoomBooking.confirmedCheckinDate)
      || !areDatesEqual(newConfirmedDates.confirmedCheckoutDate, hotelRoomBooking.confirmedCheckoutDate);
  }
}

